import { ErrorKind } from "types/analytics";
import { UsageBuffer } from "./usage-buffer";

/**
 * Captures client-side errors into the usage buffer.
 *
 * Uncaught React render errors reach the window "error" listener too, since
 * React rethrows anything no error boundary handles. Hard crashes (the WebView
 * dying, the tab being killed) cannot be observed from inside the page — the
 * embedded device infers those from a session whose usage reports stop arriving
 * without a `final: true` report.
 */

const MAX_TARGET_DESCRIPTION = 120;

const describeElement = (target: EventTarget | null): string | null => {
  const element = target as HTMLElement | null;
  if (!element || !element.tagName) return null;

  const source =
    (element as HTMLImageElement).currentSrc ||
    (element as HTMLImageElement).src ||
    (element as HTMLLinkElement).href ||
    "";

  return `${element.tagName.toLowerCase()} ${source}`.slice(
    0,
    MAX_TARGET_DESCRIPTION
  );
};

const messageOf = (value: any): string => {
  if (!value) return "Unknown error";
  if (typeof value === "string") return value;
  if (value.message) return String(value.message);

  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

export const startErrorTracking = (buffer: UsageBuffer): (() => void) => {
  const onError = (event: ErrorEvent) => {
    // Failed subresources (images, fonts, ...) surface as error events on the
    // element itself and carry no message
    if (!event.message && event.target && event.target !== window) {
      buffer.recordError({
        kind: ErrorKind.Resource,
        message: "Failed to load resource",
        source: describeElement(event.target),
      });
      return;
    }

    buffer.recordError({
      kind: ErrorKind.Error,
      message: event.message || messageOf(event.error),
      source: event.filename ?? null,
      line: event.lineno ?? null,
      column: event.colno ?? null,
      stack: event.error?.stack ?? null,
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    buffer.recordError({
      kind: ErrorKind.UnhandledRejection,
      message: messageOf(event.reason),
      stack: event.reason?.stack ?? null,
    });
  };

  // Capture phase so resource errors, which do not bubble, are seen
  window.addEventListener("error", onError as any, true);
  window.addEventListener("unhandledrejection", onUnhandledRejection as any);

  return () => {
    window.removeEventListener("error", onError as any, true);
    window.removeEventListener(
      "unhandledrejection",
      onUnhandledRejection as any
    );
  };
};
